// lib/azure-shared.ts

export const GUEST_SESSION_USER_ID = 'guest-session';
export const userFilesContainerName = 'user-files';
export const storageAccountName = 'faterstoragefrance';

export type ProjectStorageOwnerIdentifiers = {
    userId?: string | null;
    authId?: string | null;
};

export type AzureBlobDownloadResult = {
    buffer: Buffer;
    contentType: string | null;
    container: string;
    blobPath: string;
};

export function sanitizeBlobName(name: string): string {
    // Applied repeatedly until the string stops changing. A single pass was bypassable: the
    // forbidden-character strip runs after the traversal strip, so characters removed by the
    // later rule could rebuild a "../" the earlier rule had already checked. '.<.>/foo' came
    // out as '../foo'. Each pass only deletes characters, so length is non-increasing and this
    // always terminates.
    let out = name;
    let previous: string;
    do {
        previous = out;
        out = out
            .replace(/^\/+|\/+$/g, '')
            .replace(/\/+/g, '/')
            .replace(/\.\.\//g, '')
            .replace(/[<>"|?*]/g, '');
    } while (out !== previous);
    return out;
}

export function getExtensionFromMime(mime: string | null): string | undefined {
    if (!mime) return undefined;
    const normalizedMime = mime.toLowerCase().split(';')[0]?.trim();
    const mimeMap: { [key: string]: string } = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/svg+xml': 'svg',
        'application/pdf': 'pdf',
        'video/mp4': 'mp4',
        'video/webm': 'webm',
        'audio/mpeg': 'mp3',
        'audio/ogg': 'ogg',
        'audio/wav': 'wav',
        'text/plain': 'txt',
        'text/html': 'html',
        'text/css': 'css',
        'application/json': 'json',
        'application/xml': 'xml',
        'application/zip': 'zip',
        'application/octet-stream': 'bin',
    };
    return mimeMap[normalizedMime || ''];
}

export function isAzureBlobHostname(hostname: string): boolean {
    return /\.blob\.core\.windows\.net$/i.test(hostname);
}

export function buildAssetApiPath(ownerId: string, fileKey: string): string {
    const trimmedOwnerId = ownerId.trim();
    if (!trimmedOwnerId) {
        throw new Error('Invalid asset owner id');
    }
    const sanitizedKey = sanitizeBlobName(fileKey);
    if (!sanitizedKey) {
        throw new Error('Invalid asset file key');
    }
    const encodedKey = sanitizedKey
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');
    return `/api/assets/${encodeURIComponent(trimmedOwnerId)}/${encodedKey}`;
}

/**
 * Le segment `ownerId` d'une URL `/api/assets/<ownerId>/…` désigne-t-il la session invitée ?
 *
 * Les lecteurs d'assets écrivaient chacun ce prédicat contre le littéral `'GUEST_SESSION'`, qui
 * n'a jamais correspondu aux URLs réelles : elles portent {@link GUEST_SESSION_USER_ID}, soit
 * `guest-session`, tiret et non underscore. Le prédicat vit donc ici, à côté de la constante
 * qu'il compare, pour qu'aucune copie ne puisse à nouveau diverger d'elle.
 */
export function isGuestAssetOwner(ownerId: string): boolean {
    return ownerId.trim().toLowerCase() === GUEST_SESSION_USER_ID;
}

export function isInternalAssetUrl(url: unknown): url is string {
    return typeof url === 'string' && (url.startsWith('/api/assets/') || url.startsWith('/api/user-files/'));
}

export function extractBlobPathFromInternalUrl(url: string): string | null {
    if (!isInternalAssetUrl(url)) {
        return null;
    }
    if (url.startsWith('/api/assets/')) {
        return decodeURIComponent(url.substring('/api/assets/'.length));
    }
    if (url.startsWith('/api/user-files/')) {
        return decodeURIComponent(url.substring('/api/user-files/'.length));
    }
    return null;
}

/**
 * Normalize a legacy internal asset URL that encoded its sub-folder separator as
 * `%2F` (e.g. `/api/assets/<owner>/slides%2Ffile.png`). That encoded-slash form
 * reaches the route under `next dev` (which decodes it) but 404s on the production
 * Azure App Service front-end, which rejects encoded slashes in the path before
 * the route runs, so every `<img>` pointing at such a URL breaks only on the
 * deployed build. The blob itself lives at `<owner>/slides/file.png` (a real
 * sub-folder), so decoding `%2F` back to `/` yields the correct, prod-safe path
 * that the catch-all `[...fileName]` route serves. New uploads already emit real
 * slashes via buildAssetApiPath, so this is a no-op for them.
 */
export function normalizeAssetApiUrl<T extends string | null | undefined>(url: T): T {
    if (!isInternalAssetUrl(url)) {
        return url;
    }
    return url.replace(/%2F/gi, '/') as T;
}

/**
 * Deep-clone a value, rewriting every internal asset URL string it contains via
 * {@link normalizeAssetApiUrl}. Used at the slides deck read boundary so existing
 * decks (whose JSONB doc holds `%2F`-encoded snapshot/element URLs) render on the
 * deployed build without a data migration. Only strings that look like internal
 * asset URLs are touched; everything else is passed through unchanged.
 */
export function deepNormalizeAssetApiUrls<T>(value: T): T {
    if (typeof value === 'string') {
        return normalizeAssetApiUrl(value) as unknown as T;
    }
    if (Array.isArray(value)) {
        return value.map((item) => deepNormalizeAssetApiUrls(item)) as unknown as T;
    }
    if (value && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
            out[key] = deepNormalizeAssetApiUrls(val);
        }
        return out as T;
    }
    return value;
}

export function getProjectStorageOwnerCandidates(owner: ProjectStorageOwnerIdentifiers): string[] {
    const candidates: string[] = [];
    const addCandidate = (value?: string | null) => {
        if (typeof value !== 'string') {
            return;
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return;
        }
        if (!candidates.includes(trimmed)) {
            candidates.push(trimmed);
        }
    };
    addCandidate(owner.userId);
    addCandidate(owner.authId);
    return candidates;
}

export function sanitizeForFilename(name: string): string {
    if (!name) return 'untitled';
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[<>:"/\\|?*~&%#]/g, '')
        .replace(/[^a-z0-9-.]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function getBlobNameForUserFile(user_id: string, file_key: string): string {
    // Uses the same sanitiser as buildAssetBlobName. The local single-pass strip this replaced
    // was bypassable exactly like the one sanitizeBlobName's fixed-point loop closed: '....//x'
    // lost its inner '../' and left an outer one behind, so the key escaped the owner prefix.
    const cleanUserId = user_id.replace(/^\/|\/$/g, '');
    return `${cleanUserId}/${sanitizeBlobName(file_key)}`;
}

export function buildAssetBlobName(ownerId: string, fileKey: string): string {
    const cleanOwnerId = ownerId.replace(/^\/|\/$/g, '');
    const sanitizedKey = sanitizeBlobName(fileKey);
    if (!sanitizedKey) {
        throw new Error('Invalid asset file key');
    }
    return `${cleanOwnerId}/${sanitizedKey}`;
}
